// The phone's touch timeline (spec docs/specs/editor/phone-timeline.md): its rules, pure so they
// are tested without a browser. The playhead stays in the middle, so a scroll is a time and a time
// is a scroll; two fingers set the zoom; a finger held still long enough lifts a clip.
import { MAX_ZOOM, MIN_ZOOM } from "./TimelineToolbar";

/** How long a finger stays on a clip, nearly still, before it lifts the clip to drag it. */
export const LONG_PRESS_MS = 500;
/** How far a finger may wander and still be a long-press: past it, the finger scrolls. */
export const LONG_PRESS_SLOP_PX = 8;

/** The zoom two fingers set: the start's, times how far they have spread, in the toolbar's range. */
export function pinchZoom(startZoom: number, startDistance: number, distance: number): number {
  if (startDistance <= 0) return startZoom;
  const zoom = Math.round(((startZoom * distance) / startDistance) * 100) / 100;
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom));
}

/** The pinch's readout, as the card draws it: "180%". */
export function zoomLabel(zoom: number): string {
  return `${Math.round(zoom * 100)}%`;
}

/** Whether a press held `heldMs`, moved `movedPx`, is a long-press. */
export function isLongPress(heldMs: number, movedPx: number): boolean {
  return heldMs >= LONG_PRESS_MS && movedPx < LONG_PRESS_SLOP_PX;
}

/** The time under the middle when the timeline has scrolled `scrollLeft` pixels. */
export function timeAtCentre(scrollLeft: number, pxPerSecond: number): number {
  return Math.max(0, (scrollLeft / pxPerSecond) * 1000);
}

/** The scroll that puts `ms` under the middle: the inverse of `timeAtCentre`. */
export function scrollForTime(ms: number, pxPerSecond: number): number {
  return (Math.max(0, ms) / 1000) * pxPerSecond;
}

/** How fast a lifted clip held at the timeline's edge scrolls it: 8 px a frame at 60 fps. */
export const EDGE_PX_PER_S = 480;
/** The longest frame an edge step counts, so a stall never jumps the timeline. */
export const EDGE_LONGEST_FRAME_MS = 100;

/**
 * How far the edge scroll moves in a frame `elapsedMs` after its last (null for its first, a 60th of
 * a second): as far as the frame's time at `EDGE_PX_PER_S`, so a slow frame keeps the speed (spec
 * edge-scroll-time.md).
 */
export function edgeStepPx(elapsedMs: number | null): number {
  const ms = elapsedMs === null ? 1000 / 60 : Math.min(EDGE_LONGEST_FRAME_MS, Math.max(0, elapsedMs));
  return (EDGE_PX_PER_S * ms) / 1000;
}
